import { NavLink, Link, Outlet, useLocation } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext.js';
import { useRatingRealtime } from '../hooks/useRatingRealtime.js';
import { ErrorBoundary } from './ErrorBoundary.js';
import { useTheme } from '../contexts/ThemeContext.js';
import { UserLink } from './UserLink.js';

function navClass({ isActive }: { isActive: boolean }): string {
  return isActive ? 'l-header__link is-active' : 'l-header__link';
}

export function Layout() {
  const { session, profile, isStaff, signOut } = useAuth();
  const { theme, toggle } = useTheme();
  const location = useLocation();

  // レート確定を受け取って、レートを出している画面を取り直させる。
  // Layout はどのページでも表示されるので、購読はここ 1 か所で足りる。
  useRatingRealtime();

  return (
    <div className="l-app">
      {/* SPEC 76: キーボード利用者がナビゲーションを飛ばせるようにする。 */}
      <a className="c-skip-link" href="#main">
        本文へスキップ
      </a>

      <header className="l-header">
        <div className="l-header__inner">
          <Link to="/" className="l-header__brand">
            CodeRush
          </Link>

          <nav className="l-header__nav" aria-label="メインナビゲーション">
            <NavLink to="/problems" className={navClass}>
              問題
            </NavLink>
            <NavLink to="/contests" className={navClass}>
              コンテスト
            </NavLink>
            <NavLink to="/submissions" className={navClass}>
              提出
            </NavLink>
            <NavLink to="/ranking" className={navClass}>
              ランキング
            </NavLink>
            <NavLink to="/help" className={navClass}>
              ヘルプ
            </NavLink>
            {isStaff && (
              <NavLink to="/admin" className={navClass}>
                管理
              </NavLink>
            )}
          </nav>

          <div className="l-header__spacer" />

          <div className="l-header__actions">
            <button
              type="button"
              className="c-button c-button--ghost c-button--sm"
              onClick={toggle}
              aria-label={theme === 'dark' ? 'ライトテーマに切り替え' : 'ダークテーマに切り替え'}
              title={theme === 'dark' ? 'ライトテーマ' : 'ダークテーマ'}
            >
              {theme === 'dark' ? '☀' : '☾'}
            </button>

            {session && profile ? (
              <>
                <UserLink username={profile.username} displayName={profile.displayName} rating={profile.rating} />
                <Link to="/settings" className="l-header__link">
                  設定
                </Link>
                <button
                  type="button"
                  className="c-button c-button--sm"
                  onClick={() => void signOut()}
                >
                  ログアウト
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="c-button c-button--sm">
                  ログイン
                </Link>
                <Link to="/signup" className="c-button c-button--sm c-button--primary">
                  登録
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="l-main" id="main">
        {/*
          ページ単位でエラーを受け止める。
          ヘッダーとフッターを境界の外に置いているので、
          中身が壊れても移動手段は残る。
        */}
        <ErrorBoundary resetKey={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </main>

      <footer className="l-footer">
        <div className="l-footer__inner">
          <span>CodeRush — 競技プログラミングのオンラインジャッジ</span>
          <span className="u-row u-row--wrap">
            <Link to="/problems">問題</Link>
            <Link to="/contests">コンテスト</Link>
            <Link to="/ranking">ランキング</Link>
            <Link to="/help">ヘルプ</Link>
          </span>
        </div>
      </footer>
    </div>
  );
}
